import { useCallback, useEffect, useRef, type MutableRefObject } from 'react';

import { isMilkdownContextNotFoundError } from './constants';

/** Keep ref.current in sync with value (avoids stale closures without triggering re-renders) */
export function useLatestRef<T>(value: T): MutableRefObject<T> {
  const ref = useRef(value);
  ref.current = value;

  return ref;
}

/** Suppress async Milkdown collab errors during cleanup */
export function useCleanupErrorSuppression() {
  const isCleaningUpRef = useRef(false);

  useEffect(() => {
    const controller = new AbortController();

    window.addEventListener(
      'error',
      (event: ErrorEvent) => {
        const error = event.error ?? event.message;
        if (isCleaningUpRef.current && isMilkdownContextNotFoundError(error)) {
          event.preventDefault();
          event.stopPropagation();
        }
      },
      { signal: controller.signal },
    );

    return () => {
      setTimeout(() => controller.abort(), 100);
    };
  }, []);

  return useCallback(() => {
    isCleaningUpRef.current = true;
  }, []);
}

/** Safely destroy resources, optionally handling errors */
export const safeDestroy = (fn: () => void, onError?: (e: unknown) => void) => {
  try {
    fn();
  } catch (e) {
    onError?.(e);
  }
};
